import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface ProductOption {
  id: number;
  name: string;
  type: string;
  price: number;
  price_type: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  car_names: string[] | null;
  coverage_limit: string | null;
  customer_burden: string | null;
  created_at: string;
  updated_at: string;
}

interface CarName {
  carname: string;
}

interface OptionFormData {
  name: string;
  type: string;
  price: string;
  price_type: string;
  description: string;
  is_active: boolean;
  sort_order: string;
  apply_to_all: boolean;
  car_names: string[];
  coverage_limit: string;
  customer_burden: string;
}

export const OptionManagementPage: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOption, setEditingOption] = useState<ProductOption | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<OptionFormData>({
    name: '',
    type: 'insurance',
    price: '',
    price_type: 'daily',
    description: '',
    is_active: true,
    sort_order: '0',
    apply_to_all: true,
    car_names: [],
    coverage_limit: '',
    customer_burden: '',
  });

  const { data: options = [], isLoading, error } = useQuery<ProductOption[]>({
    queryKey: ['product-options', typeFilter, isActiveFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter) params.append('type', typeFilter);
      if (isActiveFilter !== null) params.append('is_active', isActiveFilter.toString());

      const response = await apiClient.get<ProductOption[]>(`/product-options?${params.toString()}`);
      return response.data;
    },
  });

  const { data: carNamesData } = useQuery({
    queryKey: ['carNames'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/vehicles/names');
      return response.data;
    },
  });

  const carNames: CarName[] = carNamesData?.data || [];

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiClient.post('/product-options', data);
      return response.data;
    },
    onSuccess: () => {
      alert('옵션이 생성되었습니다.');
      setShowCreateModal(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['product-options'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '옵션 생성에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiClient.put(`/product-options/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      alert('옵션이 수정되었습니다.');
      setEditingOption(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['product-options'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '옵션 수정에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      const response = await apiClient.put(`/product-options/${id}`, { is_active });
      return response.data;
    },
    onSuccess: () => {
      alert('옵션 상태가 변경되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['product-options'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '상태 변경에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/product-options/${id}`);
    },
    onSuccess: () => {
      alert('옵션이 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['product-options'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '옵션 삭제에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'insurance',
      price: '',
      price_type: 'daily',
      description: '',
      is_active: true,
      sort_order: '0',
      apply_to_all: true,
      car_names: [],
      coverage_limit: '',
      customer_burden: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      name: formData.name,
      type: formData.type,
      price: parseFloat(formData.price),
      price_type: formData.price_type,
      description: formData.description || null,
      is_active: formData.is_active,
      sort_order: parseInt(formData.sort_order),
      car_names: formData.apply_to_all ? null : formData.car_names,
      coverage_limit: formData.coverage_limit || null,
      customer_burden: formData.customer_burden || null,
    };

    if (editingOption) {
      updateMutation.mutate({ id: editingOption.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (option: ProductOption) => {
    setEditingOption(option);
    setFormData({
      name: option.name,
      type: option.type,
      price: option.price.toString(),
      price_type: option.price_type,
      description: option.description || '',
      is_active: option.is_active,
      sort_order: option.sort_order.toString(),
      apply_to_all: !option.car_names || option.car_names.length === 0,
      car_names: option.car_names || [],
      coverage_limit: option.coverage_limit || '',
      customer_burden: option.customer_burden || '',
    });
    setShowCreateModal(true);
  };

  const handleToggleActive = (option: ProductOption) => {
    if (window.confirm(`옵션을 ${option.is_active ? '비활성화' : '활성화'} 하시겠습니까?`)) {
      toggleActiveMutation.mutate({ id: option.id, is_active: !option.is_active });
    }
  };

  const handleDelete = (option: ProductOption) => {
    if (window.confirm(`정말로 "${option.name}" 옵션을 삭제하시겠습니까?`)) {
      deleteMutation.mutate(option.id);
    }
  };

  const optionTypeLabels: Record<string, string> = {
    insurance: '보험',
    pickup: '픽업/배송',
    equipment: '장비',
    service: '서비스',
    other: '기타',
  };

  const priceTypeLabels: Record<string, string> = {
    daily: '일일',
    hourly: '시간당',
    onetime: '1회',
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
        setEditingOption(null);
        resetForm();
      }
    };

    if (showCreateModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showCreateModal]);

  return (
    <Layout>
      <div className="px-2 py-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">옵션 관리</h1>
          <button
            onClick={() => {
              resetForm();
              setEditingOption(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
          >
            + 옵션 추가
          </button>
        </div>

        {/* Filter Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-2">
            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                옵션 종류
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                <option value="insurance">보험</option>
                <option value="pickup">픽업/배송</option>
                <option value="equipment">장비</option>
                <option value="service">서비스</option>
                <option value="other">기타</option>
              </select>
            </div>

            <div className="w-28">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                활성 상태
              </label>
              <select
                value={isActiveFilter === null ? '' : isActiveFilter.toString()}
                onChange={(e) => {
                  setIsActiveFilter(e.target.value === '' ? null : e.target.value === 'true');
                }}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            <button
              onClick={() => {
                setTypeFilter('');
                setIsActiveFilter(null);
              }}
              className="w-16 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              옵션 목록
              {options.length > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({options.length}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">옵션 조회에 실패했습니다.</p>
            </div>
          ) : options.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-slate-500 text-sm">옵션이 없습니다.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">순서</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이름</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">종류</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">가격</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">가격 유형</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">적용 차종</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">상태</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">액션</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {options.slice((page - 1) * limit, page * limit).map((option) => (
                    <tr key={option.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {option.sort_order}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {option.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                          {optionTypeLabels[option.type]}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {Math.floor(option.price).toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                          {priceTypeLabels[option.price_type]}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {!option.car_names || option.car_names.length === 0 ? (
                          <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded text-xs font-medium">전체 차종</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{option.car_names.length}개 차종</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          option.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {option.is_active ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => handleEdit(option)}
                            className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                            title="수정"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                              <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleToggleActive(option)}
                            className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                            title={option.is_active ? '비활성화' : '활성화'}
                          >
                            {option.is_active ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="m4.9 4.9 14.2 14.2"></path>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(option)}
                            className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                            title="삭제"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              <line x1="10" x2="10" y1="11" y2="17"></line>
                              <line x1="14" x2="14" y1="11" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {options.length > limit && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-600">
                      페이지 <span className="font-medium text-gray-900">{page}</span> / {Math.ceil(options.length / limit)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      이전
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(options.length / limit)}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      다음
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingOption ? '옵션 수정' : '옵션 추가'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        옵션 이름 *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                        placeholder="예: 종합보험, 공항 픽업"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        옵션 종류 *
                      </label>
                      <select
                        required
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      >
                        <option value="insurance">보험</option>
                        <option value="pickup">픽업/배송</option>
                        <option value="equipment">장비</option>
                        <option value="service">서비스</option>
                        <option value="other">기타</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        가격 *
                      </label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        가격 유형 *
                      </label>
                      <select
                        required
                        value={formData.price_type}
                        onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      >
                        <option value="daily">일일</option>
                        <option value="hourly">시간당</option>
                        <option value="onetime">1회</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      설명
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder="옵션에 대한 상세 설명"
                    />
                  </div>

                  {/* 보험 옵션 전용 필드 */}
                  {formData.type === 'insurance' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          보상한도
                        </label>
                        <input
                          type="text"
                          value={formData.coverage_limit}
                          onChange={(e) => setFormData({ ...formData, coverage_limit: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                          placeholder="예: 사고당 2천만원"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          고객부담금
                        </label>
                        <input
                          type="text"
                          value={formData.customer_burden}
                          onChange={(e) => setFormData({ ...formData, customer_burden: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                          placeholder="예: 면책 0원"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      정렬 순서
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder="0"
                    />
                    <p className="mt-1 text-xs text-gray-500">숫자가 작을수록 먼저 표시됩니다</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      적용 대상 *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={formData.apply_to_all}
                          onChange={() => setFormData({ ...formData, apply_to_all: true, car_names: [] })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">전체 차종</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!formData.apply_to_all}
                          onChange={() => setFormData({ ...formData, apply_to_all: false })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">특정 차종</span>
                      </label>
                    </div>
                  </div>

                  {!formData.apply_to_all && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        차종 선택 ({formData.car_names.length}개 선택됨)
                      </label>
                      <div className="border rounded-md p-3 max-h-60 overflow-y-auto bg-gray-50">
                        {carNames.length === 0 ? (
                          <p className="text-sm text-gray-500">차종 목록을 불러오는 중...</p>
                        ) : (
                          <div className="space-y-1.5">
                            {carNames.map((car, index) => (
                              <label key={index} className="flex items-center hover:bg-gray-100 p-2 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.car_names.includes(car.carname)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        car_names: [...formData.car_names, car.carname]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        car_names: formData.car_names.filter(c => c !== car.carname)
                                      });
                                    }
                                  }}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-900">
                                  {car.carname}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      활성 상태
                    </label>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        활성화
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingOption(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                  >
                    {editingOption ? '수정' : '생성'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
